import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { routes } from 'router/routes';
import { useSignupDirectCtx } from 'domains/signupDirect/context';

type FlowType = 'single' | 'split' | 'splitV2' | undefined;

type ProgressInfoType = {
  flowType?: FlowType;
  step: number;
  totalSteps: number;
};

type ProgressType = {
  progress: number;
  step?: number;
};

const evalScheduleTypes: { [key: string]: FlowType } = {
  single: 'single',
  'split-by-therapist': 'split',
  'split-by-slot': 'splitV2',
};

const computeProgress = (pathname: string, hint?: FlowType): ProgressInfoType => {
  const isNoProgressPage = (pathname: string) =>
    pathname.startsWith(routes.signupDirect.landingPage) ||
    pathname.startsWith(routes.signupDirect.resume) ||
    pathname.startsWith(routes.signupDirect.jotformRedirect) ||
    pathname.startsWith(routes.signupDirect.evaluation.schedule.split.experimentFork);

  // No progress for landing, resume, jotform redirect, and experiment fork pages
  if (isNoProgressPage(pathname)) {
    return { step: 0, totalSteps: 0 };
  }

  // Filters page
  if (pathname.startsWith(routes.signupDirect.filters)) {
    let totalSteps = 5; // Maximum number of steps (splitV2) by default

    if (hint === 'split') {
      totalSteps = 4;
    } else if (hint === 'single') {
      totalSteps = 3;
    }

    return { step: 1, totalSteps: totalSteps };
  }

  // Single page
  if (pathname.startsWith(routes.signupDirect.evaluation.schedule.single)) {
    return { step: 2, totalSteps: 3, flowType: 'single' };
  }

  // Split V1 first page
  if (pathname.startsWith(routes.signupDirect.evaluation.schedule.split.first)) {
    return { step: 2, totalSteps: 4, flowType: 'split' };
  }

  // Split V1 second page
  if (pathname.startsWith(routes.signupDirect.evaluation.schedule.split.second)) {
    return { step: 3, totalSteps: 4, flowType: 'split' };
  }

  // Split V2 first page
  if (pathname.startsWith(routes.signupDirect.evaluation.schedule.split.v2.first)) {
    return { step: 2, totalSteps: 5, flowType: 'splitV2' };
  }

  // Split V2 second page
  if (pathname.startsWith(routes.signupDirect.evaluation.schedule.split.v2.second)) {
    return { step: 3, totalSteps: 5, flowType: 'splitV2' };
  }

  // Split V2 therapist
  if (pathname.startsWith(routes.signupDirect.evaluation.schedule.split.v2.therapist)) {
    return { step: 4, totalSteps: 5, flowType: 'splitV2' };
  }

  // Evaluation details page
  if (pathname.startsWith(routes.signupDirect.evaluation.details)) {
    if (hint === 'single') {
      return { step: 3, totalSteps: 3, flowType: 'single' };
    }

    if (hint === 'split') {
      return { step: 4, totalSteps: 4, flowType: 'split' };
    }

    if (hint === 'splitV2') {
      return { step: 5, totalSteps: 5, flowType: 'splitV2' };
    }

    return { step: 3, totalSteps: 3 };
  }

  // Consult details page
  if (pathname.startsWith(routes.signupDirect.consultation.details)) {
    return { step: 2, totalSteps: 2 };
  }

  return { step: 0, totalSteps: 0 };
};

export const useSignupDirectProgress = (): ProgressType => {
  const { evaluationSchedule } = useSignupDirectCtx();

  const location = useLocation();

  const pathname = location.pathname;

  let hint: FlowType = undefined;

  if (evaluationSchedule?.type) {
    hint = evalScheduleTypes[evaluationSchedule?.type];
  }

  const progressInfo = useMemo(() => computeProgress(pathname, hint), [pathname, hint]);

  const currentStep = progressInfo.step ?? 0;

  const resolvedTotal = progressInfo.totalSteps;

  const percent = resolvedTotal > 0 ? Math.round((currentStep / resolvedTotal) * 100) : 0;

  return {
    progress: percent,
    step: currentStep,
  };
};
